import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AdjustmentsHorizontalIcon,
  ArrowTopRightOnSquareIcon,
  ChartBarSquareIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError, api } from "@/lib/apiClient";
import { useCurrentUser, userHasGroup, userIsRealAppAdmin } from "@/lib/auth";
import {
  deleteMaintenanceAsset,
  flattenMaintenanceCampaignAssets,
  getContractAssetsBundle,
  getMaintenanceCampaignAssets,
  getMaintenanceContract,
  getPublishedFormConfig,
  getSubLocationPlans,
  getSubLocations,
  countAssetsForSubLocation,
  deleteSubLocation,
  lookupAssets,
  maintenanceContractCustomerLabel,
  type CampaignScopedSubLocation,
  type MaintenanceCampaignAssetRow,
  type MaintenanceContract,
  type SubLocation,
} from "@/lib/wartungen";
import {
  canEditCompletedMaintenance,
  isMaintenanceCompleted,
  maintenanceEventId,
  maintenanceStartButtonLabel,
} from "./lib/maintenanceEdit";
import { contractDisplayLabel, contractRelationLabel, formatSchedule } from "./lib/campaignView";
import { normalizeFields, type NormalizedField } from "./form/runtimeModel";
import { AssetStammdatenDialog } from "./huelleDetail/AssetStammdatenDialog";
import { AssetStatusBadge } from "./huelleDetail/AssetStatusBadge";
import { CreateSubLocationDialog } from "./huelleDetail/CreateSubLocationDialog";
import { HuelleAssetTable } from "./huelleDetail/HuelleAssetTable";
import { HuelleColumnsDialog } from "./huelleDetail/HuelleColumnsDialog";
import { HuelleHeader } from "./huelleDetail/HuelleHeader";
import { RenameMaintenanceCampaignDialog } from "./components/RenameMaintenanceCampaignDialog";
import { HuellePlanViewer } from "./huelleDetail/HuellePlanViewer";
import { PlanManagementDialog } from "./huelleDetail/PlanManagementDialog";
import { SubLocationNavigator } from "./huelleDetail/SubLocationNavigator";
import {
  assetMatchesStatusFilter,
  countAssetStatuses,
  type AssetStatusFilter,
} from "./huelleDetail/assetStatus";
import "./huelleDetail/huelleDetail.css";
import {
  buildMasterdataColumnDefs,
  getAvailableHuelleColumns,
  getDefaultHuelleColumns,
  masterdataColumnLabelMap,
  normalizeHuelleColumnSelection,
} from "./huelleDetail/columns";
import { useHuelleTableSettings, useMediaQuery } from "./huelleDetail/useHuelleTableSettings";
import {
  buildSubLocationPathIndex,
  dedupeAssetsById,
  normalizeAssetPlanPosition,
  normalizePlanPointRecord,
  normalizePlanRecord,
  pathKeyFromSegments,
  resolveAssetPath,
  toPathSegments,
  type HuelleAsset,
  type MaintenancePlanRecord,
  type PlanPoint,
} from "./huelleDetail/planModel";

type ViewMode = "list" | "plan";
type FeedbackTone = "info" | "error" | "warning" | "success";

interface CampaignMeta {
  name: string;
  typeName: string;
  dateMode: string;
  dateValue: string;
  locationName: string;
  buildingName: string;
}

function num(params: URLSearchParams, key: string): number | null {
  const n = Number(params.get(key));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function hasSchedule(mode: string, value: string): boolean {
  return ["day", "month", "year"].includes(mode.trim().toLowerCase()) && value.trim() !== "";
}

function isPrefix(prefix: string[], full: string[]): boolean {
  if (!prefix.length) return true;
  if (prefix.length > full.length) return false;
  return prefix.every((s, i) => String(full[i] || "") === String(s || ""));
}

function resolveLastPerformedAt(row: MaintenanceCampaignAssetRow): string | null {
  // Anzeige = Formularabschluss-/Speicherdatum, nicht der fachliche Wartungstermin
  // (`performed_at` ist bei date_mode=year oft der 1.1. des Wartungsjahres).
  const recent = Array.isArray(row.recent_maintenances) ? row.recent_maintenances : [];
  const latest = recent.length ? recent[0] : null;
  const fromRecent = latest?.completed_at ?? latest?.updated_at ?? latest?.performed_at ?? null;
  if (fromRecent) return fromRecent;
  if (row.is_completed === true) {
    return row.completed_at ?? row.updated_at ?? row.performed_at ?? null;
  }
  return null;
}

function mapCampaignAssetRow(row: MaintenanceCampaignAssetRow): HuelleAsset {
  const masterdata =
    row.masterdata && typeof row.masterdata === "object" ? (row.masterdata as Record<string, unknown>) : undefined;
  return {
    asset_id: Number(row.asset_id),
    external_id: row.external_id ?? null,
    name: row.asset_name ?? null,
    status: row.asset_status ?? null,
    sub_location_id: row.sub_location_id ?? null,
    location_label: row.sub_location_name ?? null,
    last_performed_at: resolveLastPerformedAt(row),
    maintenance_event_id: row.maintenance_event_id ?? null,
    current_maintenance_event_id: row.maintenance_event_id ?? null,
    result_status: row.result_status ?? null,
    current_maintenance_result_status: row.result_status ?? null,
    grade_final: row.grade_final ?? null,
    current_maintenance_grade_final: row.grade_final ?? null,
    current_maintenance_completed: row.is_completed === true,
    current_maintenance_has_form_data: row.current_maintenance_has_form_data === true,
    current_maintenance_started: row.current_maintenance_started === true,
    plan_label_symbol: row.plan_label_symbol ?? null,
    masterdata,
    recent_maintenances: Array.isArray(row.recent_maintenances) ? row.recent_maintenances : [],
  };
}

function campaignScopedSubLocationIds(scopedFromCampaign: CampaignScopedSubLocation[], assets: HuelleAsset[]): number[] {
  const fromCampaign = scopedFromCampaign
    .map((row) => Number(row.sub_location_id))
    .filter((id) => Number.isFinite(id) && id > 0);
  // Stockwerke mit Objekten im Plan gehören in die Bereichsliste, auch wenn sie
  // nicht in maintenance_campaign_sub_location verknüpft sind (z. B. 6.OG/7.OG).
  const fromAssets = assets.map((asset) => Number(asset.sub_location_id)).filter((id) => Number.isFinite(id) && id > 0);
  return [...new Set([...fromCampaign, ...fromAssets])];
}

function filterSubLocationsForScope(all: SubLocation[], scopedIds: number[]): SubLocation[] {
  if (!scopedIds.length) return all;
  const includeIds = new Set(scopedIds);
  const byId = new Map<number, SubLocation>();
  for (const entry of all) {
    const id = Number(entry.id);
    if (Number.isFinite(id) && id > 0) byId.set(id, entry);
  }
  for (const id of scopedIds) {
    let current = byId.get(id);
    while (current) {
      const parentId = Number((current as { parent_id?: number }).parent_id);
      if (!Number.isFinite(parentId) || parentId <= 0 || includeIds.has(parentId)) break;
      includeIds.add(parentId);
      current = byId.get(parentId);
    }
  }
  return all.filter((entry) => {
    const id = Number(entry.id);
    return Number.isFinite(id) && id > 0 && includeIds.has(id);
  });
}

function resolveCampaignSubLocationId(scopedIds: number[], assets: HuelleAsset[], preferredId: number | null): number | null {
  if (preferredId && scopedIds.includes(preferredId)) return preferredId;
  if (scopedIds.length === 1) return scopedIds[0] ?? null;
  const fromAssets = assets.map((asset) => Number(asset.sub_location_id)).find((id) => Number.isFinite(id) && id > 0);
  if (fromAssets) return fromAssets;
  return scopedIds[0] ?? null;
}

function readCampaignMeta(row: Record<string, unknown>): CampaignMeta {
  return {
    name: String(row.name || "").trim(),
    typeName: String(row.maintenance_type_name || "").trim(),
    dateMode: String(row.date_mode || "").trim().toLowerCase(),
    dateValue: String(row.date_value || "").trim(),
    locationName: String(row.location_name || "").trim(),
    buildingName: String(row.building_name || "").trim(),
  };
}

export function HuelleDetailPage() {
  const [params, setParams] = useSearchParams();
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const canDeleteAsset = userIsRealAppAdmin(user) || userHasGroup(user, "App-Admins");

  const contractId = num(params, "contract_id");
  const projectId = num(params, "project_id");
  const typeKey = (params.get("type_key") || params.get("maintenance_type_key") || params.get("type") || "").trim();
  const campaignId = num(params, "campaign_id");
  const queryHeroAddressId = num(params, "hero_address_id");
  const rawDateMode = (params.get("date_mode") || "").trim().toLowerCase();
  const rawDateValue = (params.get("date_value") || "").trim();
  const dateMode = hasSchedule(rawDateMode, rawDateValue) ? rawDateMode : "";
  const dateValue = hasSchedule(rawDateMode, rawDateValue) ? rawDateValue : "";
  const maintenanceTitle = (params.get("maintenance_title") || "").trim();
  const baseLocationNameParam = (params.get("base_location_name") || "").trim();
  const preselectedAssetId = num(params, "asset_id");
  const initialSubLocationId = num(params, "sub_location_id");
  const isFresh = params.get("fresh") === "1";

  const valid = contractId != null && projectId != null && Boolean(typeKey);

  /** Rücksprung-URL für Unterseiten (Objekt anlegen/bearbeiten). */
  const returnUrl = useMemo(() => {
    const usp = new URLSearchParams(routerLocation.search);
    usp.delete("fresh");
    usp.delete("asset_id");
    const qs = usp.toString();
    return `${routerLocation.pathname}${qs ? `?${qs}` : ""}`;
  }, [routerLocation.pathname, routerLocation.search]);

  /** Auswertung dieser Wartung (Noten, Mängel, Material) – gleicher Kontext wie die Hülle. */
  const auswertungHref = useMemo(() => {
    if (!campaignId) return null;
    const usp = new URLSearchParams(routerLocation.search);
    usp.delete("fresh");
    usp.delete("asset_id");
    const qs = usp.toString();
    return `/wartungen/auswertung${qs ? `?${qs}` : ""}`;
  }, [campaignId, routerLocation.search]);

  const [heroAddressId, setHeroAddressId] = useState<number | null>(queryHeroAddressId);
  const [baseLocationName, setBaseLocationName] = useState(baseLocationNameParam);
  const [campaignMeta, setCampaignMeta] = useState<CampaignMeta | null>(null);
  const [contract, setContract] = useState<MaintenanceContract | null>(null);
  const [requirePlanPlacement, setRequirePlanPlacement] = useState(false);
  const [projectCompleted, setProjectCompleted] = useState<boolean>(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [assets, setAssets] = useState<HuelleAsset[]>([]);
  const [subLocations, setSubLocations] = useState<SubLocation[]>([]);
  const [plans, setPlans] = useState<MaintenancePlanRecord[]>([]);
  const [activePlanId, setActivePlanId] = useState<number | null>(null);
  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const [textFilter, setTextFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<AssetStatusFilter>("all");
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(preselectedAssetId);
  const [detailAssetId, setDetailAssetId] = useState<number | null>(null);
  const [selectedSubLocationId, setSelectedSubLocationId] = useState<number | null>(initialSubLocationId);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [deletingAsset, setDeletingAsset] = useState(false);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{ tone: FeedbackTone; message: string } | null>(null);
  const [startingAssetId, setStartingAssetId] = useState<number | null>(null);
  const [createSubLocationOpen, setCreateSubLocationOpen] = useState(false);
  const [planManagementOpen, setPlanManagementOpen] = useState(false);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [deletingSubLocation, setDeletingSubLocation] = useState(false);
  const [masterdataSchemaFields, setMasterdataSchemaFields] = useState<NormalizedField[]>([]);
  const [freshHintDismissed, setFreshHintDismissed] = useState(false);
  const { settings, saveSettings, onColumnWidthChange } = useHuelleTableSettings();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isNarrow = useMediaQuery("(max-width: 1023px)");

  const { idByPathKey, pathById } = useMemo(() => buildSubLocationPathIndex(subLocations), [subLocations]);

  const subLocationPaths = useMemo(
    () => subLocations.map((entry) => toPathSegments(entry)).filter((path) => path.length > 0),
    [subLocations],
  );

  const assetPath = useCallback((asset: HuelleAsset): string[] => resolveAssetPath(asset, pathById), [pathById]);

  const navigateToPath = useCallback(
    (path: string[]) => {
      const nextPath = Array.isArray(path) ? [...path] : [];
      setSelectedPath(nextPath);
      const subId = nextPath.length ? idByPathKey.get(pathKeyFromSegments(nextPath)) ?? null : null;
      setSelectedSubLocationId(subId);
    },
    [idByPathKey],
  );

  const loadPlans = useCallback(async (addrId: number, subLocationId: number) => {
    try {
      const raw = await getSubLocationPlans(addrId, subLocationId);
      const normalized = raw
        .map((plan) => normalizePlanRecord(plan as MaintenancePlanRecord))
        .filter(Boolean) as MaintenancePlanRecord[];
      setPlans(normalized);
      const active = normalized.find((plan) => plan.is_active) || normalized[0] || null;
      setActivePlanId((prev) =>
        prev && normalized.some((p) => Number(p.id) === Number(prev)) ? prev : (active?.id ?? null),
      );
    } catch {
      setPlans([]);
      setActivePlanId(null);
    }
  }, []);

  const load = useCallback(async () => {
    if (!valid || contractId == null || projectId == null) {
      setLoading(false);
      setFeedback({
        tone: "error",
        message: "Diese Wartung kann nicht geöffnet werden, weil Vertrag, Projekt oder Kategorie fehlen. Bitte über die Wartungsübersicht öffnen.",
      });
      return;
    }
    setLoading(true);
    // Vertragsdaten (Kunde/Nummer) parallel und unkritisch laden
    getMaintenanceContract(contractId)
      .then(setContract)
      .catch(() => setContract(null));
    try {
      let scopedSubLocationIds: number[] = [];
      let loadedAssets: HuelleAsset[] = [];
      let resolvedHeroAddressId = queryHeroAddressId;
      let locationFallback = baseLocationNameParam;

      if (campaignId) {
        const assetsPromise = getMaintenanceCampaignAssets(campaignId, { compact: true });
        const schemaPromise = getPublishedFormConfig({ typeKey, contractId, heroAddressId: resolvedHeroAddressId })
          .then((schema) => normalizeFields(schema).filter((f) => f.effective_scope === "masterdata"))
          .catch(() => [] as NormalizedField[]);
        const earlySubLocsPromise = resolvedHeroAddressId
          ? getSubLocations(resolvedHeroAddressId).catch(() => [] as SubLocation[])
          : null;
        const [campaignAssetsResult, schemaFields] = await Promise.all([assetsPromise, schemaPromise]);
        setMasterdataSchemaFields(schemaFields);
        const campaignRow = (campaignAssetsResult.campaign || {}) as Record<string, unknown>;
        setCampaignMeta(readCampaignMeta(campaignRow));
        setProjectCompleted(campaignRow.project_completed === true);
        setRequirePlanPlacement(campaignAssetsResult.require_plan_placement === true);
        if (!resolvedHeroAddressId) {
          const fromCampaign = Number(campaignRow.hero_address_id);
          if (Number.isFinite(fromCampaign) && fromCampaign > 0) resolvedHeroAddressId = fromCampaign;
        }
        loadedAssets = dedupeAssetsById(flattenMaintenanceCampaignAssets(campaignAssetsResult).map(mapCampaignAssetRow));
        scopedSubLocationIds = campaignScopedSubLocationIds(campaignAssetsResult.scoped_sub_locations || [], loadedAssets);
        locationFallback =
          baseLocationNameParam ||
          String(campaignRow.building_name || campaignRow.location_name || "").trim() ||
          locationFallback;

        setAssets(loadedAssets);
        if (locationFallback) setBaseLocationName(String(locationFallback));

        if (resolvedHeroAddressId) {
          setHeroAddressId(resolvedHeroAddressId);
          let allSubLocs: SubLocation[] = earlySubLocsPromise ? await earlySubLocsPromise : [];
          if (!earlySubLocsPromise) {
            try {
              allSubLocs = await getSubLocations(resolvedHeroAddressId);
            } catch {
              allSubLocs = [];
            }
          }
          const filteredSubLocs = filterSubLocationsForScope(allSubLocs, scopedSubLocationIds);
          setSubLocations(filteredSubLocs);
          const resolvedSubLocationId = resolveCampaignSubLocationId(scopedSubLocationIds, loadedAssets, initialSubLocationId);
          if (resolvedSubLocationId) {
            setSelectedSubLocationId(resolvedSubLocationId);
            const location = filteredSubLocs.find((entry) => Number(entry.id) === resolvedSubLocationId);
            if (location) setSelectedPath(toPathSegments(location));
          }
        }

        setFeedback(null);
        return;
      }

      // Ohne Kampagne: Projekt-/Vertragsbundle + Lookup (Legacy-Einstieg)
      const schemaPromise = getPublishedFormConfig({ typeKey, contractId, heroAddressId: resolvedHeroAddressId })
        .then((schema) => normalizeFields(schema).filter((f) => f.effective_scope === "masterdata"))
        .catch(() => [] as NormalizedField[]);
      const bundle = await getContractAssetsBundle(projectId, typeKey);
      const project = bundle.project || {};
      setProjectCompleted(project.project_completed === true);
      if (!resolvedHeroAddressId) {
        const fromProject = Number(project.hero_address_id);
        if (Number.isFinite(fromProject) && fromProject > 0) resolvedHeroAddressId = fromProject;
      }
      const bundleContract = (bundle.contracts || []).find((entry) => Number(entry.contract_id) === contractId) || null;
      if (!bundleContract) throw new Error("Der Vertrag ist diesem Projekt nicht zugeordnet.");
      setRequirePlanPlacement(Boolean(bundleContract.require_plan_placement ?? bundleContract.sub_location_plan_placement_required));
      const contractAssets = (bundleContract.assets || []) as HuelleAsset[];
      const [lookupResult, schemaFields] = await Promise.all([
        lookupAssets(typeKey, {
          contract_id: contractId,
          project_id: projectId,
          hero_address_id: resolvedHeroAddressId ?? undefined,
          date_mode: dateMode || undefined,
          date_value: dateValue || undefined,
          sub_location_id: initialSubLocationId ?? undefined,
          limit: 1000,
          offset: 0,
          q: "",
        }),
        schemaPromise,
      ]);
      setMasterdataSchemaFields(schemaFields);
      const lookupItems = (Array.isArray(lookupResult.items) ? lookupResult.items : []) as HuelleAsset[];
      loadedAssets = dedupeAssetsById([...(lookupItems.length ? lookupItems : contractAssets), ...contractAssets]);
      locationFallback =
        baseLocationNameParam ||
        String((bundleContract as { location_name?: string }).location_name || "") ||
        String(project.name || "") ||
        locationFallback;

      setAssets(loadedAssets);
      if (locationFallback) setBaseLocationName(String(locationFallback));

      if (resolvedHeroAddressId) {
        setHeroAddressId(resolvedHeroAddressId);
        let allSubLocs: SubLocation[] = [];
        try {
          allSubLocs = await getSubLocations(resolvedHeroAddressId);
        } catch {
          allSubLocs = [];
        }
        setSubLocations(filterSubLocationsForScope(allSubLocs, scopedSubLocationIds));
        if (initialSubLocationId) {
          setSelectedSubLocationId(initialSubLocationId);
          const location = allSubLocs.find((entry) => Number(entry.id) === initialSubLocationId);
          if (location) setSelectedPath(toPathSegments(location));
        }
      }

      setFeedback(null);
    } catch (err) {
      setFeedback({
        tone: "error",
        message: err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Objektdaten konnten nicht geladen werden.",
      });
    } finally {
      setLoading(false);
    }
  }, [valid, typeKey, contractId, projectId, queryHeroAddressId, campaignId, dateMode, dateValue, initialSubLocationId, baseLocationNameParam]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (initialSubLocationId && subLocations.length) {
      const location = subLocations.find((entry) => Number(entry.id) === initialSubLocationId);
      if (location) navigateToPath(toPathSegments(location));
    }
  }, [initialSubLocationId, subLocations, navigateToPath]);

  useEffect(() => {
    if (heroAddressId && selectedSubLocationId) {
      void loadPlans(heroAddressId, selectedSubLocationId);
      return;
    }
    setPlans([]);
    setActivePlanId(null);
  }, [heroAddressId, selectedSubLocationId, loadPlans]);

  // Nach dem Anlegen eines Objekts (asset_id in URL) Detail öffnen und Parameter bereinigen.
  useEffect(() => {
    if (!preselectedAssetId || loading) return;
    const match = assets.find((a) => Number(a.asset_id) === preselectedAssetId);
    if (!match) return;
    setSelectedAssetId(preselectedAssetId);
    // Liegt das Objekt in einem anderen Bereich, dorthin wechseln, damit es sichtbar ist.
    const assetSubLocationId = Number(match.sub_location_id);
    if (assetSubLocationId > 0 && assetSubLocationId !== selectedSubLocationId) {
      const path = pathById.get(assetSubLocationId);
      if (path?.length) setSelectedPath(path);
      setSelectedSubLocationId(assetSubLocationId);
    } else if (!(assetSubLocationId > 0) && selectedSubLocationId) {
      setSelectedPath([]);
      setSelectedSubLocationId(null);
    }
    setDetailAssetId(preselectedAssetId);
    const usp = new URLSearchParams(params);
    usp.delete("asset_id");
    setParams(usp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedAssetId, loading, assets.length, pathById]);

  /* ───────────── Ableitungen ───────────── */

  const scopedAssets = useMemo(
    () =>
      assets.filter((a) => {
        if (selectedSubLocationId && Number(a.sub_location_id) !== selectedSubLocationId) return false;
        return isPrefix(selectedPath, assetPath(a));
      }),
    [assets, selectedPath, selectedSubLocationId, assetPath],
  );

  const filtered = useMemo(() => {
    const tf = textFilter.trim().toLowerCase();
    return scopedAssets.filter((a) => {
      if (!assetMatchesStatusFilter(a, statusFilter)) return false;
      if (!tf) return true;
      return `${a.external_id || ""} ${a.name || ""}`.toLowerCase().includes(tf);
    });
  }, [scopedAssets, textFilter, statusFilter]);

  const scopedCounts = useMemo(() => countAssetStatuses(scopedAssets), [scopedAssets]);

  const assetCountByPathKey = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const asset of assets) {
      const path = assetPath(asset);
      if (!path.length) continue;
      const key = pathKeyFromSegments(path);
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [assets, assetPath]);

  const selectedSubLocationLabel = useMemo(() => {
    if (selectedPath.length) return selectedPath.join(" > ");
    if (selectedSubLocationId) {
      const mapped = pathById.get(selectedSubLocationId);
      if (mapped?.length) return mapped.join(" > ");
    }
    return "";
  }, [pathById, selectedPath, selectedSubLocationId]);

  const masterdataColumnDefs = useMemo(() => buildMasterdataColumnDefs(masterdataSchemaFields, assets), [masterdataSchemaFields, assets]);
  const masterdataColumns = useMemo(() => masterdataColumnDefs.map((def) => def.columnKey), [masterdataColumnDefs]);
  const columnLabels = useMemo(() => masterdataColumnLabelMap(masterdataColumnDefs), [masterdataColumnDefs]);
  const availableColumns = useMemo(() => getAvailableHuelleColumns(masterdataColumns), [masterdataColumns]);
  const visibleColumns = useMemo(() => {
    const view = isMobile ? settings.mobile : settings.desktop;
    if (view?.columns?.length) return normalizeHuelleColumnSelection(view.columns, availableColumns);
    return getDefaultHuelleColumns(masterdataColumns);
  }, [isMobile, settings, availableColumns, masterdataColumns]);
  const columnWidths = useMemo(() => {
    const view = isMobile ? settings.mobile : settings.desktop;
    return view?.widths ?? settings.desktop?.widths ?? {};
  }, [isMobile, settings]);

  const assetCountAtSelectedSubLocation = useMemo(
    () => (selectedSubLocationId ? countAssetsForSubLocation(assets, selectedSubLocationId) : 0),
    [assets, selectedSubLocationId],
  );
  const canDeleteSelectedSubLocation = Boolean(selectedSubLocationId && assetCountAtSelectedSubLocation === 0);
  const deleteSubLocationHint = !selectedSubLocationId
    ? null
    : assetCountAtSelectedSubLocation > 0
      ? `Löschen nicht möglich: ${assetCountAtSelectedSubLocation} Objekt(e) zugeordnet.`
      : null;

  const selectedAsset = useMemo(
    () => assets.find((asset) => Number(asset.asset_id) === Number(selectedAssetId)) ?? null,
    [assets, selectedAssetId],
  );
  const detailAsset = useMemo(
    () => assets.find((asset) => Number(asset.asset_id) === Number(detailAssetId)) ?? null,
    [assets, detailAssetId],
  );

  const typeLabel = campaignMeta?.typeName || typeKey || "Wartung";
  const scheduleLabel = formatSchedule(campaignMeta?.dateMode || dateMode, campaignMeta?.dateValue || dateValue);
  const locationLabel = baseLocationName || campaignMeta?.buildingName || campaignMeta?.locationName || "";
  const customerLabel = contract ? maintenanceContractCustomerLabel(contract) : "";
  const contractLabel = contract ? contractDisplayLabel(contract) : contractId ? `Vertrag #${contractId}` : "";
  const relationLabel = `${contract ? contractRelationLabel(contract) : "Vertrag"} / Projekt`;
  const projectLabel = projectId ? `Projekt #${projectId}` : "";
  const title =
    campaignMeta?.name ||
    maintenanceTitle ||
    [typeLabel, locationLabel].filter(Boolean).join(" – ") ||
    "Wartung";
  const subtitle = [typeLabel, scheduleLabel !== "–" ? scheduleLabel : "", customerLabel].filter(Boolean).join(" · ");

  /* ───────────── Aktionen ───────────── */

  function isPlanPlacementRequired(asset: HuelleAsset | null, subLocationId: number | null): boolean {
    if (!requirePlanPlacement || !asset || !subLocationId) return false;
    if (plans.length === 0) return false;
    const relevantPlanIds = new Set(plans.map((plan) => Number(plan.id)).filter((id) => id > 0));
    return !(asset.plan_positions || []).some(
      (position) => relevantPlanIds.has(Number(position.plan_id)) && position.is_positioned,
    );
  }

  function handleAssetPlanUpdated(assetId: number, point: PlanPoint, plan: MaintenancePlanRecord) {
    setAssets((prev) =>
      prev.map((asset) => {
        if (Number(asset.asset_id) !== Number(assetId)) return asset;
        const normalizedPosition = normalizeAssetPlanPosition({
          plan_id: plan.id,
          plan_title: plan.title || plan.filename || "",
          plan_page: point.page_number || plan.page_number || 1,
          plan_point: {
            x: point.x ?? null,
            y: point.y ?? null,
            label_x: point.label_x ?? null,
            label_y: point.label_y ?? null,
            coordinate_mode: "normalized",
          },
          label_text: point.label_text || "",
          label_position: { x: point.label_x ?? null, y: point.label_y ?? null, coordinate_mode: "normalized" },
        });
        if (!normalizedPosition) return asset;
        const positions = [...(asset.plan_positions || [])];
        const index = positions.findIndex((entry) => Number(entry.plan_id) === Number(plan.id));
        if (index >= 0) positions[index] = { ...positions[index], ...normalizedPosition };
        else positions.push(normalizedPosition);
        return { ...asset, plan_positions: positions, is_plan_positioned: positions.some((entry) => entry.is_positioned) };
      }),
    );
    setPlans((prev) =>
      prev.map((entry) => {
        if (Number(entry.id) !== Number(plan.id)) return entry;
        const normalized = normalizePlanPointRecord(point, entry);
        if (!normalized) return entry;
        const points = [...(entry.points || [])];
        const index = points.findIndex((p) => Number(p.asset_id) === Number(assetId));
        if (index >= 0) points[index] = { ...points[index], ...normalized };
        else points.push(normalized);
        return { ...entry, points };
      }),
    );
  }

  function navigateToMaintenanceForm(
    eventId: number,
    asset: HuelleAsset,
    subLocationId: number | null,
    options?: { view?: boolean; typeKey?: string },
  ) {
    const usp = new URLSearchParams();
    usp.set("type_key", options?.typeKey || typeKey);
    usp.set("asset_id", String(asset.asset_id));
    usp.set("project_id", String(projectId!));
    usp.set("contract_id", String(contractId!));
    usp.set("maintenance_event_id", String(eventId));
    if (dateMode && dateValue) {
      usp.set("date_mode", dateMode);
      usp.set("date_value", dateValue);
    }
    if (campaignId) usp.set("campaign_id", String(campaignId));
    if (subLocationId) usp.set("sub_location_id", String(subLocationId));
    if (options?.view) {
      usp.set("view", "1");
      usp.set("return_url", returnUrl);
    } else if (canEditCompletedMaintenance(asset, projectCompleted)) {
      usp.set("edit_completed", "1");
    }
    navigate(`/wartungen/form?${usp.toString()}`);
  }

  function buildEditHref(assetId: number, subLocationId: number | null): string {
    const usp = new URLSearchParams({
      mode: "edit",
      type_key: typeKey,
      asset_id: String(assetId),
      return_to: "huelle_detail",
      return_url: returnUrl,
    });
    if (contractId) usp.set("contract_id", String(contractId));
    if (heroAddressId) usp.set("hero_address_id", String(heroAddressId));
    if (subLocationId) usp.set("sub_location_id", String(subLocationId));
    return `/wartungen/objekt-anlegen?${usp.toString()}`;
  }

  const createHref = (() => {
    const usp = new URLSearchParams({ type_key: typeKey, after_create: "huelle_detail", return_url: returnUrl });
    if (contractId) usp.set("contract_id", String(contractId));
    if (heroAddressId) usp.set("hero_address_id", String(heroAddressId));
    if (selectedSubLocationId) usp.set("sub_location_id", String(selectedSubLocationId));
    return `/wartungen/objekt-anlegen?${usp.toString()}`;
  })();

  async function startMaintenanceFor(asset: HuelleAsset | null) {
    if (!asset || !asset.asset_id) {
      setFeedback({ tone: "warning", message: "Bitte zuerst ein Objekt auswählen." });
      return;
    }
    const assetId = Number(asset.asset_id);
    const subLocationId =
      asset.sub_location_id != null && Number(asset.sub_location_id) > 0 ? Number(asset.sub_location_id) : selectedSubLocationId;
    setSelectedAssetId(assetId);
    if (subLocationId) setSelectedSubLocationId(subLocationId);

    if (isMaintenanceCompleted(asset) && projectCompleted) {
      setFeedback({ tone: "warning", message: "Das verknüpfte Projekt ist fertiggestellt. Die Wartung kann nicht mehr bearbeitet werden." });
      return;
    }
    if (isPlanPlacementRequired(asset, subLocationId)) {
      setDetailAssetId(null);
      setViewMode("plan");
      setFeedback({
        tone: "warning",
        message: "Laut Vertrag muss das Objekt zuerst auf dem Plan des Unterorts platziert werden. Klicke im Plan auf die Position des Objekts.",
      });
      return;
    }

    setStartingAssetId(assetId);
    setFeedback(null);
    try {
      const existingEventId = maintenanceEventId(asset);
      if (canEditCompletedMaintenance(asset, projectCompleted) && existingEventId) {
        navigateToMaintenanceForm(existingEventId, asset, subLocationId);
        return;
      }
      const body: Record<string, unknown> = {
        asset_id: assetId,
        project_id: projectId,
        contract_id: contractId,
        sub_location_id: subLocationId || null,
        intent: "save",
        date_mode: dateMode,
        date_value: dateValue,
      };
      if (campaignId) body.campaign_id = campaignId;
      if (heroAddressId) body.hero_address_id = heroAddressId;
      const result = await api.post<{ maintenance_event_id?: number }>(
        `/maintenance-types/${encodeURIComponent(typeKey)}/maintenance-events/start`,
        body,
      );
      const eventId = Number(result.maintenance_event_id);
      if (!Number.isFinite(eventId) || eventId <= 0) throw new Error("maintenance_event_id fehlt.");
      navigateToMaintenanceForm(eventId, asset, subLocationId);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const body = err.body as { detail?: { error_code?: string; message?: string } } | null;
        const detail = body?.detail && typeof body.detail === "object" ? body.detail : null;
        if (detail?.error_code === "MASTERDATA_REQUIRED_MISSING") {
          navigate(buildEditHref(assetId, subLocationId));
          return;
        }
        if (detail?.error_code === "PLAN_PLACEMENT_REQUIRED") {
          setDetailAssetId(null);
          setViewMode("plan");
          setFeedback({
            tone: "warning",
            message: detail.message || "Das Objekt muss zuerst auf einem Unterort-Plan positioniert werden.",
          });
          return;
        }
      }
      setFeedback({
        tone: "error",
        message: err instanceof ApiError ? err.message : "Wartung konnte nicht gestartet werden.",
      });
    } finally {
      setStartingAssetId(null);
    }
  }

  async function handleSubLocationCreated(created: { id: number; path: string[] }) {
    if (heroAddressId) {
      const refreshed = await getSubLocations(heroAddressId);
      setSubLocations(refreshed);
      const match = refreshed.find((entry) => Number(entry.id) === created.id);
      const path = match ? toPathSegments(match) : created.path;
      navigateToPath(path);
      setSelectedSubLocationId(created.id);
    }
    setFeedback({
      tone: "success",
      message: "Unterort angelegt und ausgewählt. Optional kannst du jetzt einen Plan hochladen, um Objekte darauf zu platzieren.",
    });
    setPlanManagementOpen(true);
  }

  async function handleDeleteSubLocation() {
    if (!heroAddressId || !selectedSubLocationId) return;
    if (!canDeleteSelectedSubLocation) {
      setFeedback({
        tone: "warning",
        message: deleteSubLocationHint || "Unterort kann nicht gelöscht werden, solange Objekte zugeordnet sind.",
      });
      return;
    }
    const label = selectedSubLocationLabel || `Unterort ${selectedSubLocationId}`;
    if (!window.confirm(`Unterort „${label}“ wirklich löschen?`)) return;

    setDeletingSubLocation(true);
    setFeedback(null);
    try {
      await deleteSubLocation(heroAddressId, selectedSubLocationId);
      const parentPath = selectedPath.slice(0, -1);
      const refreshed = await getSubLocations(heroAddressId);
      setSubLocations(refreshed);
      navigateToPath(parentPath);
      setFeedback({ tone: "success", message: "Unterort wurde gelöscht." });
    } catch (err) {
      setFeedback({ tone: "error", message: err instanceof ApiError ? err.message : "Unterort konnte nicht gelöscht werden." });
    } finally {
      setDeletingSubLocation(false);
    }
  }

  async function handleDeleteAsset(asset: HuelleAsset | null) {
    if (!canDeleteAsset || !asset?.asset_id || !typeKey) return;
    const assetId = Number(asset.asset_id);
    const label = String(asset.external_id || asset.name || `#${assetId}`).trim();
    if (
      !window.confirm(
        `Objekt „${label}“ wirklich löschen?\n\nDas Objekt verschwindet aus Web- und iOS-App. Abgeschlossene Wartungshistorie bleibt erhalten.`,
      )
    ) {
      return;
    }
    setDeletingAsset(true);
    setFeedback(null);
    try {
      await deleteMaintenanceAsset(typeKey, assetId);
      setAssets((prev) => prev.filter((entry) => Number(entry.asset_id) !== assetId));
      setSelectedAssetId((current) => (current === assetId ? null : current));
      setDetailAssetId((current) => (current === assetId ? null : current));
      setFeedback({ tone: "success", message: "Objekt wurde gelöscht und ist nach dem nächsten Sync auch in der iOS-App entfernt." });
    } catch (err) {
      setFeedback({ tone: "error", message: err instanceof ApiError ? err.message : "Objekt konnte nicht gelöscht werden." });
    } finally {
      setDeletingAsset(false);
    }
  }

  function selectAsset(assetId: number, subLocationId?: number | null) {
    setSelectedAssetId(assetId);
    if (subLocationId) setSelectedSubLocationId(subLocationId);
  }

  function openAssetDetail(assetId: number, subLocationId?: number | null) {
    selectAsset(assetId, subLocationId);
    setDetailAssetId(assetId);
  }

  function assetSubLocationId(asset: HuelleAsset): number | null {
    return asset.sub_location_id != null && Number(asset.sub_location_id) > 0
      ? Number(asset.sub_location_id)
      : selectedSubLocationId;
  }

  function canViewMaintenance(asset: HuelleAsset): boolean {
    return isMaintenanceCompleted(asset) && maintenanceEventId(asset) != null;
  }

  function openCompletedMaintenance(asset: HuelleAsset) {
    const eventId = maintenanceEventId(asset);
    if (!eventId) return;
    navigateToMaintenanceForm(eventId, asset, assetSubLocationId(asset), { view: true });
  }

  const rowActionDisabled = (asset: HuelleAsset) => isMaintenanceCompleted(asset) && !canViewMaintenance(asset);
  const rowActionLabel = (asset: HuelleAsset) =>
    canViewMaintenance(asset) ? "Wartung ansehen" : maintenanceStartButtonLabel(asset, projectCompleted);

  const hasAnyAssets = assets.length > 0;
  const listFilterActive = statusFilter !== "all" || textFilter.trim() !== "";
  const showFreshHint = isFresh && !freshHintDismissed && !loading;

  function renderAssetActions(asset: HuelleAsset | null) {
    if (!asset) return null;
    const assetId = Number(asset.asset_id);
    const subLocationId = asset.sub_location_id != null ? Number(asset.sub_location_id) : selectedSubLocationId;
    const existingEventId = maintenanceEventId(asset);
    const viewable = canViewMaintenance(asset);
    const canEdit = !isMaintenanceCompleted(asset) || canEditCompletedMaintenance(asset, projectCompleted);
    const disabled = rowActionDisabled(asset);
    return (
      <>
        {canDeleteAsset ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-status-red"
            disabled={deletingAsset || startingAssetId === assetId}
            onClick={() => void handleDeleteAsset(asset)}
            leftIcon={<TrashIcon className="h-4 w-4" aria-hidden />}
          >
            {deletingAsset ? "Lösche …" : "Löschen"}
          </Button>
        ) : null}
        <Link
          to={buildEditHref(assetId, subLocationId)}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-card px-3 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
        >
          <PencilSquareIcon className="h-4 w-4" aria-hidden />
          Stammdaten bearbeiten
        </Link>
        {viewable && existingEventId ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => openCompletedMaintenance(asset)}
            leftIcon={<ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />}
          >
            Wartung ansehen
          </Button>
        ) : null}
        {canEdit ? (
          <Button
            type="button"
            size="sm"
            disabled={disabled || startingAssetId === assetId}
            loading={startingAssetId === assetId}
            onClick={() => void startMaintenanceFor(asset)}
          >
            {maintenanceStartButtonLabel(asset, projectCompleted)}
          </Button>
        ) : null}
      </>
    );
  }

  return (
    <AppShell
      title={title}
      subtitle={valid ? subtitle || undefined : undefined}
      back={{ to: "/wartungen", label: "zurück zur Übersicht" }}
      actions={
        valid && auswertungHref ? (
          <Link
            to={auswertungHref}
            className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-card px-4 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque hover:bg-fill-tertiary"
            title="Noten, Mängel und Material dieser Wartung auswerten"
          >
            <ChartBarSquareIcon className="h-4 w-4" aria-hidden />
            Auswertung
          </Link>
        ) : undefined
      }
    >
      {feedback ? (
        <Alert
          variant={feedback.tone === "error" ? "error" : feedback.tone === "warning" ? "warning" : feedback.tone === "success" ? "success" : "info"}
          className="mb-4"
        >
          {feedback.message}
        </Alert>
      ) : null}

      {showFreshHint ? (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-ios border border-accent/30 bg-accent/10 px-4 py-3">
          <div className="text-sm text-label">
            <p className="font-semibold">Die Wartung wurde angelegt.</p>
            <p className="mt-0.5 text-label-secondary">
              Nächste Schritte: 1. Objekte anlegen (oder aus einer früheren Wartung übernehmen) · 2. optional Unterorte und
              Pläne pflegen · 3. je Objekt „Wartung durchführen“ und das Formular ausfüllen.
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg p-1 text-label-secondary hover:bg-fill-tertiary hover:text-label"
            aria-label="Hinweis ausblenden"
            onClick={() => setFreshHintDismissed(true)}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="huelle-panel flex items-center gap-2 px-4 py-8 text-sm text-label-secondary">
          <Spinner /> Lade Wartung …
        </div>
      ) : !valid ? null : (
        <div className="space-y-4">
          <HuelleHeader
            title={title}
            onRename={campaignId && !projectCompleted ? () => setRenameOpen(true) : undefined}
            categoryLabel={typeLabel}
            scheduleLabel={scheduleLabel}
            locationLabel={
              selectedSubLocationLabel ? `${locationLabel ? `${locationLabel} › ` : ""}${selectedSubLocationLabel}` : locationLabel
            }
            customerLabel={customerLabel}
            contractLabel={contractLabel}
            relationLabel={relationLabel}
            projectLabel={projectLabel}
            projectCompleted={projectCompleted}
            counts={scopedCounts}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
          {campaignId ? (
            <RenameMaintenanceCampaignDialog
              open={renameOpen}
              campaignId={campaignId}
              initialName={title}
              onClose={() => setRenameOpen(false)}
              onSaved={(name) => {
                setCampaignMeta((prev) =>
                  prev
                    ? { ...prev, name }
                    : {
                        name,
                        typeName: typeLabel,
                        dateMode,
                        dateValue,
                        locationName: locationLabel,
                        buildingName: "",
                      },
                );
                setParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete("maintenance_title");
                    return next;
                  },
                  { replace: true },
                );
              }}
            />
          ) : null}

          <div className="huelle-layout">
            <aside className="huelle-layout__aside min-w-0">
              <SubLocationNavigator
                compact={isNarrow}
                selectedPath={selectedPath}
                selectedSubLocationId={selectedSubLocationId}
                subLocationPaths={[...subLocationPaths, ...assets.map(assetPath).filter((path) => path.length > 0)]}
                onNavigate={(path) => {
                  navigateToPath(path);
                  setSelectedAssetId(null);
                }}
                onCreateClick={() => setCreateSubLocationOpen(true)}
                onManagePlansClick={() => setPlanManagementOpen(true)}
                onDeleteClick={() => void handleDeleteSubLocation()}
                canManage={Boolean(heroAddressId)}
                canDeleteSelected={canDeleteSelectedSubLocation}
                deleteDisabledHint={deleteSubLocationHint}
                deleting={deletingSubLocation}
                assetCountByPathKey={assetCountByPathKey}
                planCount={plans.length}
              />
            </aside>

            <section className="huelle-panel min-w-0 overflow-hidden" aria-label="Objekte">
              <div className="huelle-toolbar">
                <div className="flex flex-wrap items-center gap-2">
                  <SegmentedControl<ViewMode>
                    name="Ansicht"
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                      { value: "list", label: "Liste" },
                      {
                        value: "plan",
                        label: (
                          <span className="inline-flex items-center gap-1">
                            Plan
                            {selectedSubLocationId && plans.length ? (
                              <span className="eh-segmented-item__badge">{plans.length}</span>
                            ) : null}
                          </span>
                        ),
                      },
                    ]}
                  />
                  <span className="huelle-meta-pill" aria-live="polite">
                    {filtered.length === scopedAssets.length
                      ? `${scopedAssets.length} Objekt${scopedAssets.length === 1 ? "" : "e"}`
                      : `${filtered.length} von ${scopedAssets.length} Objekten`}
                  </span>
                  {listFilterActive ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                      onClick={() => {
                        setStatusFilter("all");
                        setTextFilter("");
                      }}
                    >
                      <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
                      Filter zurücksetzen
                    </button>
                  ) : null}
                </div>
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-1 sm:justify-end">
                  <div className="huelle-toolbar__search">
                    <MagnifyingGlassIcon className="huelle-toolbar__search-icon" aria-hidden />
                    <input
                      className="eh-input huelle-toolbar__search-input min-w-0"
                      placeholder="Kennung oder Bezeichnung …"
                      value={textFilter}
                      onChange={(e) => setTextFilter(e.target.value)}
                      aria-label="Objekte durchsuchen"
                    />
                  </div>
                  {viewMode === "list" ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setColumnsDialogOpen(true)}
                      leftIcon={<AdjustmentsHorizontalIcon className="h-4 w-4" aria-hidden />}
                      title="Sichtbare Spalten anpassen"
                    >
                      Spalten
                    </Button>
                  ) : null}
                  {!projectCompleted ? (
                    <Link
                      to={createHref}
                      className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-accent px-4 text-sm font-medium text-label-on-accent shadow-sm hover:bg-accent-hover"
                    >
                      <PlusIcon className="h-4 w-4" aria-hidden />
                      Objekt anlegen
                    </Link>
                  ) : null}
                </div>
              </div>

              {viewMode === "plan" ? (
                <div className="huelle-action-bar">
                  {selectedAsset ? (
                    <>
                      <div className="huelle-selected-chip" title="Ausgewähltes Objekt">
                        <span className="huelle-selected-chip__mark" aria-hidden>
                          ✓
                        </span>
                        <span className="huelle-selected-chip__text">
                          {String(selectedAsset.external_id || selectedAsset.name || `#${selectedAssetId}`)}
                        </span>
                      </div>
                      <AssetStatusBadge asset={selectedAsset} />
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:justify-end">
                        <Button type="button" variant="secondary" size="sm" onClick={() => setDetailAssetId(Number(selectedAsset.asset_id))}>
                          Details
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={rowActionDisabled(selectedAsset) || startingAssetId != null}
                          loading={startingAssetId === Number(selectedAsset.asset_id)}
                          onClick={() =>
                            canViewMaintenance(selectedAsset)
                              ? openCompletedMaintenance(selectedAsset)
                              : void startMaintenanceFor(selectedAsset)
                          }
                        >
                          {rowActionLabel(selectedAsset)}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <span className="huelle-selected-hint">
                      {selectedSubLocationId
                        ? "Objekt im Plan oder in der Liste antippen, um es auszuwählen oder zu platzieren."
                        : "Wähle links einen Unterort mit Plan, um Objekte darauf zu platzieren."}
                    </span>
                  )}
                </div>
              ) : null}

              {viewMode === "list" ? (
                !hasAnyAssets ? (
                  <div className="border-t border-separator px-6 py-12 text-center">
                    <p className="text-base font-semibold text-label">Noch keine Objekte in dieser Wartung</p>
                    <p className="mx-auto mt-1 max-w-md text-sm text-label-secondary">
                      Objekte sind die zu wartenden Einheiten (z. B. Türen, Anlagen). Lege sie hier an; jedes Objekt bekommt
                      danach eine eigene Zeile mit „Wartung durchführen“.
                    </p>
                    {!projectCompleted ? (
                      <Link
                        to={createHref}
                        className="mt-5 inline-flex h-10 items-center gap-1.5 rounded-lg bg-accent px-4 text-sm font-medium text-label-on-accent shadow-sm hover:bg-accent-hover"
                      >
                        <PlusIcon className="h-4 w-4" aria-hidden />
                        Erstes Objekt anlegen
                      </Link>
                    ) : null}
                  </div>
                ) : (
                  <HuelleAssetTable
                    rows={filtered}
                    selectedAssetId={selectedAssetId}
                    selectedSubLocationLabel={selectedSubLocationLabel}
                    widths={columnWidths}
                    columns={visibleColumns}
                    columnLabels={columnLabels}
                    loading={loading}
                    onSelectAsset={selectAsset}
                    onOpenAsset={openAssetDetail}
                    onColumnWidthChange={onColumnWidthChange}
                    rowAction={{
                      label: rowActionLabel,
                      disabled: rowActionDisabled,
                      busyAssetId: startingAssetId,
                      onClick: (asset) =>
                        canViewMaintenance(asset) ? openCompletedMaintenance(asset) : void startMaintenanceFor(asset),
                    }}
                    emptyText={
                      listFilterActive ? (
                        <>
                          Kein Objekt passt zu Suche oder Statusfilter.{" "}
                          <button
                            type="button"
                            className="font-medium text-accent hover:underline"
                            onClick={() => {
                              setStatusFilter("all");
                              setTextFilter("");
                            }}
                          >
                            Filter zurücksetzen
                          </button>
                        </>
                      ) : (
                        <>
                          In diesem Bereich liegen keine Objekte.{" "}
                          <button type="button" className="font-medium text-accent hover:underline" onClick={() => navigateToPath([])}>
                            Alle Bereiche anzeigen
                          </button>
                        </>
                      )
                    }
                  />
                )
              ) : (
                <div className="border-t border-separator p-3">
                  <HuellePlanViewer
                    plans={plans}
                    activePlanId={activePlanId}
                    onActivePlanIdChange={setActivePlanId}
                    selectedSubLocationId={selectedSubLocationId}
                    assets={assets}
                    filteredAssets={filtered}
                    selectedAssetId={selectedAssetId}
                    onSelectAsset={setSelectedAssetId}
                    onAssetPlanUpdated={handleAssetPlanUpdated}
                    onFeedback={(message, tone) => setFeedback({ message, tone })}
                  />
                </div>
              )}
            </section>
          </div>
        </div>
      )}

      <AssetStammdatenDialog
        open={detailAssetId != null && detailAsset != null}
        asset={detailAsset}
        masterdataFields={masterdataSchemaFields}
        locationLabel={selectedSubLocationLabel}
        onClose={() => setDetailAssetId(null)}
        onOpenMaintenance={(entry) => {
          if (!detailAsset) return;
          const eventId = Number(entry.maintenance_event_id);
          if (!Number.isFinite(eventId) || eventId <= 0) return;
          const historyType = String(entry.maintenance_type_key || "").trim();
          const subLocationId =
            detailAsset.sub_location_id != null ? Number(detailAsset.sub_location_id) : selectedSubLocationId;
          navigateToMaintenanceForm(eventId, detailAsset, subLocationId, {
            view: true,
            typeKey: historyType || typeKey,
          });
        }}
        actions={renderAssetActions(detailAsset)}
      />

      <CreateSubLocationDialog
        open={createSubLocationOpen}
        onClose={() => setCreateSubLocationOpen(false)}
        heroAddressId={heroAddressId}
        subLocations={subLocations}
        onCreated={(created) => void handleSubLocationCreated(created)}
      />

      <PlanManagementDialog
        open={planManagementOpen}
        onClose={() => setPlanManagementOpen(false)}
        heroAddressId={heroAddressId}
        subLocationId={selectedSubLocationId}
        subLocationLabel={selectedSubLocationLabel}
        onPlansChanged={() => {
          if (heroAddressId && selectedSubLocationId) void loadPlans(heroAddressId, selectedSubLocationId);
        }}
      />

      <HuelleColumnsDialog
        open={columnsDialogOpen}
        onClose={() => setColumnsDialogOpen(false)}
        settings={settings}
        masterdataColumns={masterdataColumns}
        columnLabels={columnLabels}
        onSave={async (next) => {
          await saveSettings(next);
        }}
      />
    </AppShell>
  );
}
